<template>
    <div class='dual-y-chart'>
      <Tooltip
        :show="showTooltip"
        v-bind="tooltipContent"
        :position="tooltipPosition"
        :chartWidth="chartWidth"
        :dataWidth="chartDefaults.dataWidth"
      />
      <ChartWrapper
        :data="chartData"
        :width="chartWidth"
        :height="chartHeight"
        :dimensionKey="xDimension"
        :chartDefaults="chartDefaults"
        :primaryKey="yMetrics.left"
        :dualYAxis="true"
      >
        <template #x-axis="props">
          <XAxis
            v-bind="props"
            :scale="scale"
            :useShortLabel="useShortLabel"
          />
        </template>
        <template #y-axis="props">
          <YAxis
            v-bind="props"
            :isInPercentages="isMetricPercentage('left')"
            :customInterval="dualInterval.left"
            :customChartMax="customDualChartMax.left"
            :primaryKey="yMetrics.left"
            :textColor="chartColors.left"
            intervalStyle="stroke-dasharray: 4 2; stroke: #C1CCD4"
          />
          <YAxis
            v-bind="props"
            :rightAxis="true"
            :isInPercentages="isMetricPercentage('right')"
            :customInterval="dualInterval.right"
            :customChartMax="customDualChartMax.right"
            :primaryKey="yMetrics.right"
            :textColor="chartColors.right"
          />
        </template>
        <template #bars="props">
          <SingleBar
            v-bind="props"
            :customChartMax="customDualChartMax.left"
            :hasTransitions="true"
            :hoveredIndex="hoveredIndex"
            :primaryColor="chartColors.left"
            :barWidth="chartDefaults.dataWidth"
            @onHover="setTooltip"
            @onLeave="hideTooltip"
          />
        </template>
        <template #line-chart="props">
          <LineChart
            v-bind="props"
            :customChartMax="customDualChartMax.right"
            :hoveredIndex="hoveredIndex"
            :hideOnLeave="true"
            :showVerticals="false"
            :height="chartHeight"
            :scale="scale"
            :metric="yMetrics.right"
            :colors="[chartColors.right]"
            @onHover="setTooltip"
            @onLeave="hideTooltip"
          />
        </template>
      </ChartWrapper>
    </div>
</template>

<script>
import {
  getReportFieldFromSnake,
} from '../../../../../../services/dashboard/reportFieldService';
import {
  getInterval,
  getChartMax,
} from '../../../../../../services/dashboard/graphs/intervalsService';
import {
  getDisplayValue,
  getValueAndLabel,
} from '../../../../../../services/dashboard/displayFormatService';
import { mapState } from 'vuex';
import ChartWrapper from '../Graph/ChartWrapper.vue';
import XAxis from '../Graph/Axes/XAxis.vue';
import YAxis from '../Graph/Axes/YAxis.vue';
import LineChart from '../Graph/LineChart/LineChart.vue';
import SingleBar from '../Graph/Bars/SingleBar.vue';
import Tooltip from '../../../Tooltips/ChartTooltip/Tooltip.vue';
import { max } from 'd3-array';
import { timeParse, timeFormat } from 'd3-time-format';
export default {
  props: {
    data: {
      type: Array,
      required: true,
    },
    yMetrics: {
      type: Object,
      required: true,
    },
    xDimension: {
      type: String,
      required: true,
    },
    scale: {
      type: String,
      default: 'band',
      validator: v => {
        return ['band', 'timeseries', 'linear'].includes(v);
      },
    },
    chartWidth: {
      type: Number,
      required: true,
    },
    chartHeight: {
      type: Number,
      required: true,
    },
    chartColors: {
      type: Object,
      default: () => {
        return {
          left: '#4369F4',
          right: '#00A6A4',
        };
      },
    },
    useShortLabel: {
      type: Boolean,
      default: false,
    },
    isSorted: {
      type: Boolean,
      default: false,
    },
    chartDefaults: {
      type: Object,
      default: () => {
        return {
          dataWidth: 24,
          xLabelPadding: 16,
          yLabelPadding: 8,
          yLabelWidth: 40,
          fontSize: 12,
          margin: {
            top: 24,
            right: 0,
            bottom: 0,
            left: 0,
          },
        };
      },
    },
  },
  components: {
    Tooltip,
    ChartWrapper,
    XAxis,
    YAxis,
    LineChart,
    SingleBar,
  },
  data() {
    return {
      hoveredIndex: null,
      showTooltip: false,
      tooltipContent: {
        title: '',
        subtitle: '',
        content: [],
      },
      tooltipPosition: {},
      emptyChartMax: 11,
    };
  },
  computed: {
    ...mapState({
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
      storedStartDate: state => state.dashboard.reportRequest.startDate,
      storedEndDate: state => state.dashboard.reportRequest.endDate,
    }),
    orderedChartData() {
      if (this.isSorted) return this.data;

      switch (this.scale) {
        case 'timeSeries':
          return this.orderByDates();
        case 'linear':
          return this.orderByValue(this.xDimension);
        case 'band':
        default:
          return this.orderByValue(this.yMetrics.left);
      }
    },
    timeParsedData() {
      const parseDate = timeParse('%Y-%m-%d');

      return this.orderedChartData.map(d => {
        const obj = { ...d };

        obj[this.dimensionKey] = parseDate(d[this.dimensionKey]);

        return obj;
      });
    },
    chartData() {
      return this.scale === 'timeseries' ? this.timeParsedData : this.orderedChartData;
    },
    dualDataMax() {
      return {
        left: max(this.data, d => d[this.yMetrics.left]) || this.emptyChartMax,
        right: max(this.data, d => d[this.yMetrics.right]) || this.emptyChartMax,
      };
    },
    dualInterval() {
      return {
        left: getInterval(this.dualDataMax.left),
        right: getInterval(this.dualDataMax.right),
      };
    },
    customDualChartMax() {
      let customChartMaxLeft = getChartMax(
        this.dualDataMax.left,
        this.dualInterval.left,
      );
      let customeChartMaxRight = getChartMax(
        this.dualDataMax.right,
        this.dualInterval.right,
      );
      const leftIntervals = customChartMaxLeft / this.dualInterval.left;
      const rightIntervals = customeChartMaxRight / this.dualInterval.right;
      const intervalsDiff = leftIntervals - rightIntervals;

      if (intervalsDiff > 0) {
        customeChartMaxRight = customeChartMaxRight + (intervalsDiff * this.dualInterval.right);
      } else {
        customChartMaxLeft = customChartMaxLeft - (intervalsDiff * this.dualInterval.left);
      }

      return {
        left: customChartMaxLeft,
        right: customeChartMaxRight,
      };
    },
    tooltipTitle() {
      const fromDate = timeFormat('%b %d, %Y')(this.storedStartDate);
      const toDate = timeFormat('%b %d, %Y')(this.storedEndDate);

      return fromDate + ' - ' + toDate;
    },
    leftMetricObj() {
      const leftMetric = this.yMetrics.left;

      return getReportFieldFromSnake(this.allReportFields, leftMetric);
    },
    rightMetricObj() {
      const rightMetric = this.yMetrics.right;

      return getReportFieldFromSnake(this.allReportFields, rightMetric);
    },
  },
  methods: {
    orderByDates() {
      const orderedData = [...this.data].sort((a, b) => {
        const dateArrayA = a[this.xDimension].split('-');
        const dateArrayB = b[this.xDimension].split('-');
        const comparison = dateArrayA[0] - dateArrayB[0]
          || dateArrayA[1] - dateArrayB[1]
          || dateArrayA[2] - dateArrayB[2];

        return comparison;
      });

      return orderedData;
    },
    orderByValue(val) {
      const orderedData = [...this.data].sort((a, b) => {
        const dataA = a[val];
        const dataB = b[val];
        const comparison = dataB - dataA;

        return comparison;
      });

      return orderedData;
    },
    setTooltip(val) {
      this.tooltipPosition = val.position;
      this.hoveredIndex = val.hoveredIndex;
      this.updateTooltipContent(val.data);
      this.showTooltip = true;
    },
    updateTooltipContent(data) {
      this.tooltipContent.title = this.tooltipTitle;
      this.tooltipContent.subtitle = this.getTooltipSubtitle(data);
      this.tooltipContent.content = this.getTooltipContent(data);
    },
    getTooltipSubtitle(data) {
      return data.d && data.d[this.xDimension] || this.xDimension;
    },
    getTooltipContent(data) {
      const content = [];

      const leftValue = getDisplayValue(
        this.leftMetricObj,
        data.d && data.d[this.yMetrics.left],
      );
      const rightValue = getDisplayValue(
        this.rightMetricObj,
        data.d && data.d[this.yMetrics.right],
      );

      content.push(getValueAndLabel(leftValue, this.leftMetricObj));
      content.push(getValueAndLabel(rightValue, this.rightMetricObj));

      return content;
    },
    hideTooltip() {
      this.hoveredIndex = null;
      this.showTooltip = false;
    },
    isMetricPercentage(metric) {
      const metricObj = metric === 'left'
        ? this.leftMetricObj
        : this.rightMetricObj;

      const type = metricObj.type;

      return type === 'rate';
    },
  },
};
</script>

<style lang='scss' scoped>
.dual-y-chart {
  width: 100%;
}
</style>
